/**
 * Login page for Attorneys
 * Separate authentication from main users (Clerk) and Accountants
 * Read-only access to released cases
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Scale, ArrowLeft, FileText, Download, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AttorneyLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store token and attorney data
      localStorage.setItem('attorney_token', data.access_token);
      localStorage.setItem('attorney_profile', JSON.stringify(data.attorney));

      // Redirect to attorney dashboard
      navigate('/attorney/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1628] flex-col justify-between p-12">
        {/* Logo */}
        <div>
          <Link to="/home">
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-44 w-auto"
            />
          </Link>
        </div>

        {/* Main Content */}
        <div className="max-w-xl">
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            Attorney Portal
          </h1>
          <p className="text-lg text-blue-200 mb-8 leading-relaxed">
            Access finalized business interruption reports, review expert analysis, 
            and download court-ready documents for your cases.
          </p>

          {/* Features */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Released Reports</p>
                <p className="text-blue-200/70 text-sm">View finalized case reports when ready</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Scale className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Expert Analysis</p>
                <p className="text-blue-200/70 text-sm">Attorney & adjuster analysis for negotiations</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Court-Ready Downloads</p>
                <p className="text-blue-200/70 text-sm">PDF and Word reports for filing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-slate-500 text-sm">
          © 2026 ClaimSync. Professional business interruption claim calculations.
        </p>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/home">
              <img 
                src="/logo.svg" 
                alt="ClaimSync" 
                className="h-32 w-auto mx-auto mb-4"
              />
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-4">
              <Scale className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Attorney Login</h2>
            <p className="text-gray-600 mt-1">Access your firm's released cases</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="you@lawfirm.com"
                data-testid="attorney-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link 
                  to="/attorney/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                data-testid="attorney-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="attorney-login-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-8 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-slate-700 font-medium">Need access?</p>
                <p className="text-sm text-slate-500 mt-1">
                  Contact your ClaimSync administrator to set up attorney portal access for your firm.
                </p>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/home" 
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
